import React from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { EditableCorral } from "../types/corral.types";

const { Text, Title } = Typography;

interface CorralPreviewProps {
  editableCorrals: EditableCorral[];
  distance: string;
  eventName?: string;
  onBack: () => void;
  onConfirm: () => void;
  confirming: boolean;
  readOnly?: boolean;
  onClose?: () => void;
}



const CorralPreview: React.FC<CorralPreviewProps> = ({
  editableCorrals,
  distance,
  eventName,
  onBack,
  onConfirm,
  confirming,
  readOnly = false,
  onClose,
}) => {
  const totalRunners = editableCorrals.reduce(
    (s, c) => s + c.participants.length,
    0,
  );
  const namedCorrals = editableCorrals.filter((c) => !c.is_default);
  const defaultCorral = editableCorrals.find((c) => c.is_default);

  return (
    <div>
      {/* ── Sub-header ───────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 2 }}>
            Preview — {distance}
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {editableCorrals.length} corral{editableCorrals.length !== 1 ? "s" : ""} ·{" "}
            {totalRunners} runners
          </Text>
        </div>
        <Tag color="blue" style={{ fontSize: 13, padding: "4px 12px", borderRadius: 8 }}>
          {eventName}
        </Tag>
      </div>

      <Row gutter={[32, 32]}>
        {/* ── Left: Visual Track ──────────────────────────────────────────── */}
        <Col xs={24} md={10}>
          <Card
            bordered={false}
            style={{
              backgroundColor: "#f1f5f9",
              borderRadius: 16,
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "8px 0",
              }}
            >
              {/* Road track wrapper */}
              <div
                style={{
                  width: 160,
                  backgroundColor: "#cbd5e1",
                  borderRadius: 8,
                  overflow: "hidden",
                  position: "relative",
                  paddingBottom: 12,
                }}
              >
                {/* Dashed center line */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: "50%",
                    width: 2,
                    background:
                      "repeating-linear-gradient(to bottom, #94a3b8 0px, #94a3b8 8px, transparent 8px, transparent 16px)",
                    transform: "translateX(-50%)",
                    opacity: 0.4,
                  }}
                />

                {/* START LINE */}
                <div
                  style={{
                    backgroundColor: "#1e293b",
                    color: "#fff",
                    textAlign: "center",
                    fontWeight: 900,
                    fontSize: 11,
                    letterSpacing: 3,
                    padding: "10px 0",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  START LINE
                </div>

                {/* Connector */}
                <div
                  style={{
                    width: 3,
                    height: 14,
                    backgroundColor: "#94a3b8",
                    margin: "0 auto",
                  }}
                />

                {/* Named Corrals */}
                {namedCorrals.map((corral, i) => (
                  <React.Fragment key={corral.key}>
                    <div
                      style={{
                        backgroundColor: corral.color,
                        color: "#fff",
                        textAlign: "center",
                        padding: "10px 12px",
                        margin: "0 12px",
                        borderRadius: 6,
                        boxShadow: `0 2px 6px ${corral.color}55`,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: 13,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        CORRAL {corral.name}
                      </div>
                      {corral.start_time && (
                        <div style={{ fontSize: 10, opacity: 0.9, marginTop: 2 }}>
                          {corral.start_time}
                        </div>
                      )}
                      <div style={{ fontSize: 10, opacity: 0.8, marginTop: 1 }}>
                        {corral.participants.length} runners
                      </div>
                    </div>
                    {/* Connector between corrals */}
                    {(i < namedCorrals.length - 1 || defaultCorral) && (
                      <div
                        style={{
                          width: 3,
                          height: 14,
                          backgroundColor: "#94a3b8",
                          margin: "0 auto",
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}

                {/* Default Corral */}
                {defaultCorral && (
                  <div
                    style={{
                      backgroundColor: defaultCorral.color,
                      color: "#fff",
                      textAlign: "center",
                      padding: "10px 12px",
                      margin: "0 12px",
                      borderRadius: 6,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: 12, letterSpacing: 1 }}>
                      DEFAULT
                    </div>
                    <div style={{ fontSize: 10, opacity: 0.85, marginTop: 1 }}>
                      {defaultCorral.participants.length} runners (no BIB)
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </Col>

        {/* ── Right: Corral Info Cards ─────────────────────────────────────── */}
        <Col xs={24} md={14}>
          <Card
            bordered={false}
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: 20 }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {editableCorrals.map((corral, i) => (
                <div
                  key={corral.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 0",
                    borderBottom:
                      i < editableCorrals.length - 1
                        ? "1px solid #f1f5f9"
                        : "none",
                  }}
                >
                  {/* Color dot */}
                  <div
                    style={{
                      width: 14,
                      height: 40,
                      borderRadius: 4,
                      backgroundColor: corral.color,
                      flexShrink: 0,
                    }}
                  />

                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <Text strong style={{ fontSize: 14 }}>
                        Corral {corral.name}
                      </Text>
                      {corral.start_time && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          · {corral.start_time}
                        </Text>
                      )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {corral.is_default
                        ? "No BIB assigned (overflow)"
                        : `BIB ${corral.bib_start}–${corral.bib_end}`}
                    </Text>
                  </div>

                  {/* Runners count */}
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 20,
                        color: corral.color,
                        lineHeight: 1,
                      }}
                    >
                      {corral.participants.length}
                    </div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      / {corral.max_participants} max
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Totals summary */}
          <div
            style={{
              display: "flex",
              gap: 24,
              padding: "14px 20px",
              background: "#f8fafc",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
            }}
          >
            <div>
              <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
                Total Runners
              </Text>
              <div style={{ fontWeight: 700, fontSize: 20 }}>{totalRunners}</div>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
                Corrals
              </Text>
              <div style={{ fontWeight: 700, fontSize: 20 }}>
                {namedCorrals.length}
                {defaultCorral ? " + Default" : ""}
              </div>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
                Distance
              </Text>
              <div style={{ fontWeight: 700, fontSize: 20 }}>{distance}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      {readOnly ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 28 }}>
          <Button size="large" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 28,
          }}
        >
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            loading={confirming}
            onClick={onConfirm}
            style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
          >
            Confirm Assignment
          </Button>
        </div>
      )}
    </div>
  );
};

export default CorralPreview;
